import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import PublicIcon from '@mui/icons-material/Public';
import SatelliteIcon from '@mui/icons-material/Satellite';
import MapIcon from '@mui/icons-material/Map'; // Keep icons if used directly or remove if only passed via props

// Removed internal basemapOptions constant

const BasemapPicker = ({ basemaps = [], onSelectBasemap }) => { // Accept basemaps prop, provide default
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  // TODO: Potentially track selected basemap ID if needed for visual feedback

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (providerFactory) => {
    if (onSelectBasemap && typeof providerFactory === 'function') {
      onSelectBasemap(providerFactory);
    }
    handleClose();
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16, // Adjust as needed
        right: 16, // Adjust as needed
        zIndex: 10, // Ensure it's above the Cesium canvas
      }}
    >
      <Tooltip title="Change Basemap">
        <IconButton
          onClick={handleClick}
          size="large"
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.85),
            backdropFilter: 'blur(4px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
              transform: 'translateY(-1px)'
            },
          }}
        >
          <LayersIcon sx={{ fontSize: '1.2rem' }}/>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 6,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 10px 30px rgba(0,0,0,0.12), 0px 4px 8px rgba(0,0,0,0.06))',
            mt: 1.5,
            minWidth: 200,
            backgroundColor: alpha(theme.palette.background.paper, 0.92),
            backdropFilter: 'blur(8px)',
            borderRadius: 2,
            p: 1.5,
            '& .MuiMenuItem-root': {
              borderRadius: 1.5,
              my: 0.25,
              fontSize: '0.85rem',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              }
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{
          '& .MuiPaper-root': {
            marginTop: '8px', // Add some space between button and menu
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1 }}>
          <LayersIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
          <Box component="span" sx={{ fontWeight: 500, fontSize: '0.95rem', color: theme.palette.primary.main }}>
            Select Basemap
          </Box>
        </Box>
        
        {basemaps.map((option) => ( // Map over the basemaps prop
          <MenuItem 
            key={option.id} 
            onClick={() => handleSelect(option.providerFactory)}
            sx={{ 
              py: 0.75,
              px: 1,
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: 'translateX(2px)'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {React.cloneElement(option.icon, { 
                fontSize: 'small', 
                sx: { 
                  fontSize: '1.1rem',
                  color: theme.palette.text.secondary
                } 
              })}
            </ListItemIcon>
            <ListItemText 
              primary={option.name}
              primaryTypographyProps={{ 
                sx: { 
                  fontSize: '0.85rem',
                  fontWeight: 400 
                } 
              }} 
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default BasemapPicker;